<script setup lang="ts">
import type { MegamenuNavItem, MegamenuSection } from '~/data/megamenu'

const props = defineProps<{
  item: MegamenuNavItem
  active: boolean
}>()

const selectedSectionIndex = ref(0)

const sectionsWithContent = computed(() =>
  (props.item.sections ?? []).filter(
    (s) =>
      (s.items?.length ?? 0) > 0 ||
      (s.subSections?.length ?? 0) > 0 ||
      (s.title === 'Sales' && !s.items?.length)
  )
)

const selectedSection = computed((): MegamenuSection | null => {
  const list = sectionsWithContent.value
  if (list.length) {
    const i = Math.min(selectedSectionIndex.value, list.length - 1)
    return list[i] ?? null
  }
  // Kits etc: top-level items, no sections – show items as single pane
  if ((props.item.items?.length ?? 0) > 0) {
    return { title: '', items: props.item.items }
  }
  return null
})

watch(
  () => props.item,
  () => {
    selectedSectionIndex.value = 0
  },
  { immediate: true }
)
</script>

<template>
  <div
    data-test-id="megamenu-dropdown"
    class="absolute left-0 right-0 top-full xl:px-4 border-b border-t border-gray-200 bg-white shadow-lg"
    :class="[
      active
        ? 'z-[100] opacity-100 translate-y-0'
        : 'z-40 opacity-0 -translate-y-2 pointer-events-none invisible',
    ]"
  >
    <div
      data-test-id="megamenu-dropdown-container"
      class="max-w-screen-xl mx-auto px-4 py-2 flex"
    >
      <MegamenuDropdownNav
        :item="item"
        :selected-section-index="selectedSectionIndex"
        @select-section="selectedSectionIndex = $event"
      />
      <MegamenuDropdownContent
        :section="selectedSection"
        :menu-name="item.label"
      />
    </div>
  </div>
</template>
